import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './SugarIconSelector.css';

// import sugar icon assets
import sugarWhite from "../coffee_card/assets/sugars/sugar-white.svg";
import sugarDark from "../coffee_card/assets/sugars/sugar-dark.svg";

interface SugarIconSelectorProps {
    value?: {
        level?: string;
        type?: string;
    };
    onChange: (level: string, type: string) => void;
    showTitle?: boolean;
    className?: string;
}

const SugarIconSelector: React.FC<SugarIconSelectorProps> = ({
    value,
    onChange,
    showTitle = true,
    className = ''
}) => {
    const sugarLevels = [
        { id: 'no-sugar', label: 'No Sugar', cubes: 0 },
        { id: 'a-little', label: 'A Little', cubes: 1 },
        { id: 'medium', label: 'Medium', cubes: 2 },
        { id: 'a-lot', label: 'A Lot', cubes: 3 }
    ];

    const sugarTypes = [
        { id: 'white', label: 'White Sugar', icon: sugarWhite },
        { id: 'brown', label: 'Brown Sugar', icon: sugarDark }
    ];

    const currentLevel = value?.level || '';
    const currentType = value?.type || '';

    const handleLevelSelect = (level: string) => {
        // if selecting no-sugar, clear type as well
        if (level === 'no-sugar') {
            onChange(level, '');
        } else {
            // keep the current type if already selected, otherwise leave empty for user to choose
            onChange(level, currentType);
        }
    };

    const handleTypeSelect = (type: string) => {
        // if no level is selected, default to medium
        const levelToUse = currentLevel || 'medium';
        onChange(levelToUse, type);
    };

    const renderSugarCubes = (count: number, type: string, forDisplay: boolean = false) => {
        if (count === 0) {
            return (
                <div className="no-sugar-icon">
                    <FontAwesomeIcon icon={faTimes} />
                </div>
            );
        }

        const cubes = [];
        // for level options display, always show white cubes as preview
        // for actual selection, show the selected type or gray if not selected
        const sugarIcon = type === 'brown' ? sugarDark : sugarWhite;
        const showGray = !forDisplay && !type;

        for (let i = 0; i < count; i++) {
            cubes.push(
                <img
                    key={i}
                    src={sugarIcon}
                    alt={`${type || 'sugar'} cube`}
                    className="sugar-cube"
                    style={showGray ? { opacity: 0.3, filter: 'grayscale(100%)' } : undefined}
                />
            );
        }
        return cubes;
    };

    return (
        <div className={`sugar-icon-selector ${className}`}>
            {showTitle && <h4>Sugar Selection</h4>}

            {/* Sugar Level Selection */}
            <div className="sugar-section">
                <label className="sugar-section-label">sugar level</label>
                <div className="sugar-level-options">
                    {sugarLevels.map(level => (
                        <div
                            key={level.id}
                            className={`sugar-level-option ${currentLevel === level.id ? 'selected' : ''}`}
                            onClick={() => handleLevelSelect(level.id)}
                            title={level.label}
                        >
                            <div className="sugar-cubes-display">
                                {renderSugarCubes(level.cubes, currentType || 'white', true)}
                            </div>
                            <span className="sugar-level-label">{level.label.toLowerCase()}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Sugar Type Selection - only show if not no-sugar */}
            {currentLevel && currentLevel !== 'no-sugar' && (
                <div className="sugar-section">
                    <label className="sugar-section-label">
                        sugar type {!currentType && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                    </label>
                    <div className="sugar-type-options">
                        {sugarTypes.map(type => (
                            <div
                                key={type.id}
                                className={`sugar-type-option ${currentType === type.id ? 'selected' : ''}`}
                                onClick={() => handleTypeSelect(type.id)}
                                title={type.label}
                            >
                                <img
                                    src={type.icon}
                                    alt={type.label}
                                    className="sugar-type-icon"
                                />
                                <span className="sugar-type-label">{type.label.toLowerCase()}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SugarIconSelector; 